import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { Error } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';

import { GetFileExtensionsBySource } from '../../utils/image-helper';
import CameraModal from '../camera-modal';
import { uuid } from '../../types/common-helper';
import { PlayerContext } from '../../contexts/player';
import { GameDocumentContext } from '../../contexts/game-document';
import { GetTaskById } from '../../utils/game-document/assets/tasks';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { generateTitleById } from '../../utils/game-document/display-languages';
import { getFromStore } from '../../utils/indexedDB';
import { TFileUpload } from '../../hooks/use-upload-chunk';
import { OverlayContext } from '../../contexts/overlay';
import { useFullscreen } from '../../hooks/use-fullscreen';
import { GetTaskContentById } from '../../utils/game-document/assets/task-contents';
import { useLocalStorage } from 'usehooks-ts';

const generateRandomString = () => {
  return uuid().split('-').join('').substring(0, 5);
};

type Props = {
  isUploading: boolean;
  isReadonly?: boolean;
  selectedImage?: string;
  onSelectedImageCallback?: (blobUrl: string | File, size?: number) => void;
  isDisabled?: boolean;
  formId: string;
  taskId: string;
  isOnline: boolean;
  uploadStatus?: 'idle' | 'uploading' | 'failed' | 'complete';
  uploadError?: string | null;
  onRetry?: () => void;
  onCancel?: () => void;
};

const AnswerVideo = ({
  isUploading,
  isReadonly = false,
  selectedImage = '',
  onSelectedImageCallback = () => {},
  isDisabled = false,
  formId,
  taskId,
  isOnline,
  uploadStatus = 'idle',
  uploadError = null,
  onRetry,
  onCancel
}: Props) => {
  const [player] = useContext(PlayerContext);
  const [gameDocument] = useContext(GameDocumentContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);
  const [overlay, setOverlay] = useContext(OverlayContext);

  const inputFile = useRef<HTMLInputElement>(null);
  const [isValidFileExtension, setIsValidFileExtension] =
    useState<boolean>(true);
  const [videoKey, setVideoKey] = useState<number>(0);
  const [isFileExist, setIsFileExist] = useState(false);
  const [resumeFullscreenAfterCamera, setResumeFullscreenAfterCamera] =
    useState(false);
  const { isFullscreen, exitFullscreen } = useFullscreen();
  const [cameraOpen, setCameraOpen, removeCameraOpen] = useLocalStorage(
    'camera-open',
    false
  );

  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);

  const playerCode = player.playerState?.code;

  useEffect(() => {
    setVideoKey((prev) => prev + 1);
    if (selectedImage === '') return;

    let cancelled = false;
    let objectUrl: string | null = null;

    setBlobUrl(null);

    const fetchBlob = async () => {
      try {
        const res = await fetch(selectedImage);

        const blob = await res.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (err) {
        console.error(
          'VideoThumbnail: blob fetch failed, falling back to direct URL',
          err
        );

        if (!cancelled) setBlobUrl(selectedImage);
      }
    };

    fetchBlob();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedImage]);

  const handleSelectImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      let taskName = '';
      const task = GetTaskById(gameDocument.gameDocument, taskId);
      if (task) {
        taskName = task.name;
      } else {
        const taskContent = GetTaskContentById(
          gameDocument.gameDocument,
          taskId
        );
        taskName = taskContent?.name ?? '';
      }
      const fileName = file.name;
      const fileExtension = fileName.slice(fileName.lastIndexOf('.'));
      const newFileName = `${player!.playerState!.name}-${taskName}-${generateRandomString()}${fileExtension}`;

      setIsValidFileExtension(true);
      let extensionFound = undefined;

      if (fileExtension) {
        // set file ext checker
        let imageExtensionList = GetFileExtensionsBySource(
          'Assets - Task Content - Video Answer Type'
        );

        if (imageExtensionList) {
          extensionFound = imageExtensionList.find(
            (validFileExtension) =>
              validFileExtension ===
              fileExtension.replaceAll('.', '').toLowerCase()
          );
          setIsValidFileExtension(extensionFound ? true : false);
        }
      }

      if (extensionFound) {
        const newFile = new File([file], newFileName, { type: file.type });
        onSelectedImageCallback(newFile, newFile.size ?? 0);
        setIsFileExist(true);
        setVideoKey((prev) => prev + 1);
      } else {
        setIsFileExist(false);
        onSelectedImageCallback('');
      }
    }
  };

  const turnOnCameraClick = async () => {
    if (!overlay.isVideoPermissionGranted) {
      exitFullscreen();

      setOverlay((prev) => ({ ...prev, isVideoPermissionGranted: true }));
    }

    await new Promise((r) => setTimeout(r, 500));
    isFullscreen && setResumeFullscreenAfterCamera(isFullscreen);
    setCameraOpen(true);
    setIsValidFileExtension(true);
  };

  const onAddImageClick = () => inputFile.current && inputFile.current.click();

  const handleVideoCapture = useCallback(
    (blob: Blob, url: string) => {
      try {
        const extension = blob.type.includes('mp4') ? 'mp4' : 'webm';

        let taskName = '';
        const task = GetTaskById(gameDocument.gameDocument, taskId);
        if (task) {
          taskName = task.name;
        } else {
          const taskContent = GetTaskContentById(
            gameDocument.gameDocument,
            taskId
          );
          taskName = taskContent?.name ?? '';
        }

        const file = new File(
          [blob],
          `${player!.playerState!.name}-${taskName}-${generateRandomString()}.${extension}`,
          {
            type: blob.type
          }
        );

        onSelectedImageCallback(file, blob.size);

        setIsFileExist(true);
        setVideoKey((prev) => prev + 1);
        removeCameraOpen();
      } finally {
        URL.revokeObjectURL(url);
      }
    },
    [gameDocument.gameDocument, taskId, onSelectedImageCallback]
  );

  useEffect(() => {
    if (!playerCode) return;

    (async () => {
      const files = await getFromStore<TFileUpload>('upload-queue', playerCode);

      if (files) {
        const selectedFile = files.files.find(
          (f) => f.taskId === taskId && f.formId === formId
        );

        if (selectedFile) {
          setIsFileExist(true);

          if (selectedFile.status === 'complete') {
            if (selectedFile.url) onSelectedImageCallback(selectedFile.url);
          }
        }
      }
    })();
  }, [playerCode, taskId, formId, isUploading, isOnline]);

  return (
    <div id={'answer-file'} className={'d-flex flex-column flex-grow-1 h-100'}>
      <div
        className={
          'd-flex flex-grow-1 flex-column justify-content-center border text-center h-100 position-relative'
        }>
        <div
          className={`${isUploading && isOnline ? 'd-flex' : 'd-none'} flex-column justify-content-center align-items-center h-100 p-3`}>
          <Loader type={'infinite-spinner'} size={'large'} />
          <span className="mt-2">
            {generateTitleById(
              '71a2dffb-7ae7-422f-8423-93b0d4f15a4a',
              gameDocument,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Uploading...'}
          </span>
          {onCancel && (
            <Button
              className="mt-3 btn-sm"
              fillMode="outline"
              themeColor="base"
              onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>

        {!isOnline && (isFileExist || uploadStatus === 'failed') ? (
          <div className="d-flex flex-column justify-content-center align-items-center h-100 p-3 text-center">
            <span className="material-symbols-outlined text-primary text-size-64 mb-2">
              cloud_off
            </span>
            <span className="text-primary fw-bold text-size-18 mb-1">
              Offline - Video saved!
            </span>
            <span className="text-muted small">
              Your video is safely stored on this device. Upload will
              automatically resume once you're back online.
            </span>
          </div>
        ) : uploadStatus === 'failed' && !isUploading ? (
          <div className="d-flex flex-column justify-content-center align-items-center h-100 p-3 text-center">
            <span className="material-symbols-outlined text-danger text-size-48 mb-2">
              error
            </span>
            <span className="text-danger fw-bold mb-1">
              {uploadError ||
                'Upload failed. Please check your connection and retry.'}
            </span>
            <span className="text-muted small mb-3">
              Your video is preserved. You can retry upload or record a new
              video.
            </span>
            {onRetry && (
              <Button
                themeColor="primary"
                className="px-3 py-2"
                onClick={onRetry}>
                <span className="material-symbols-outlined mr-2">refresh</span>
                Retry Upload
              </Button>
            )}
          </div>
        ) : null}

        <div
          className={`${(isUploading && isOnline) || uploadStatus === 'failed' || (!isOnline && isFileExist) ? 'd-none' : 'd-flex'} justify-content-center align-items-center h-100`}>
          {selectedImage === '' ? (
            <div
              className={
                'd-flex flex-column align-items-center justify-content-center'
              }>
              <span
                className={
                  'material-symbols-outlined text-success text-size-64'
                }>
                videocam
              </span>
              <span>
                {generateTitleById(
                  '567963b7-47c0-40e9-8659-2e43a5a5befb',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Preview'}
              </span>
              <span className={'text-danger'}>
                Please upload a video that’s 15 seconds or less.
              </span>
            </div>
          ) : (
            <video
              width={'650'}
              height={'400'}
              key={videoKey}
              src={blobUrl ?? ''}
              preload={'auto'}
              crossOrigin={'anonymous'}
              className={'img-fluid img-thumbnail'}
              controls
              playsInline
            />
          )}
        </div>
      </div>

      {!isReadonly && (!isFileExist || isOnline) && (
        <Row className={'py-2'}>
          {!isValidFileExtension && (
            <Col className={'mb-4'} md={12}>
              <Error>
                {generateTitleById(
                  '9ff35578-4946-4900-a4a3-e76f776ab4ca',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Invalid File Type: Please upload video files only.'}
              </Error>
            </Col>
          )}
          {!isDisabled && (
            <div className={'d-flex flex-column gap-2'}>
              <Button
                className={'w-100 upload-button p-2'}
                disabled={isUploading}
                onClick={turnOnCameraClick}>
                <div
                  className={
                    'd-flex justify-content-center align-items-center gap-3'
                  }>
                  <span
                    className={'material-symbols-outlined mr-2 text-success'}
                    role={'button'}>
                    videocam
                  </span>
                  <span>
                    {generateTitleById(
                      '51689c93-1e57-4c72-9308-5deab301614a',
                      gameDocument,
                      displayLanguageContext.displayLanguageSelected.resources!,
                      'game'
                    ) || 'Take video'}
                  </span>
                </div>
              </Button>
              <Button
                className={'w-100 upload-button p-2'}
                disabled={isUploading}
                onClick={onAddImageClick}>
                <div
                  className={
                    'd-flex justify-content-center align-items-center gap-3'
                  }>
                  <span
                    className={'material-symbols-outlined mr-2 text-success'}
                    role={'button'}>
                    add_photo_alternate
                  </span>
                  <span>
                    {generateTitleById(
                      'b6d7d97b-37bd-4ef9-aa9e-0e02a404a375',
                      gameDocument,
                      displayLanguageContext.displayLanguageSelected.resources!,
                      'game'
                    ) || 'Add from files'}
                  </span>
                </div>
              </Button>
              <input
                type={'file'}
                id={'answer-file-upload'}
                className={'d-none'}
                onChange={handleSelectImage}
                onClick={(event) => {
                  event.currentTarget.value = '';
                }}
                ref={inputFile}
                accept={'video/*'}
              />
            </div>
          )}
        </Row>
      )}

      <CameraModal
        isOpen={cameraOpen}
        isResumeFullscreenAfterCamera={resumeFullscreenAfterCamera}
        onClose={removeCameraOpen}
        onCapture={() => {}}
        onVideoCapture={handleVideoCapture}
        mode="video"
      />
    </div>
  );
};

export default AnswerVideo;
